import { Button, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Filter } from 'react-bootstrap-icons';
import { useOnClickOutside } from 'usehooks-ts';
import { getSearchUrl } from '../../shared/urlBuilder';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectSearchIncludeVersions,
  selectSearchKeyword,
  selectSearchOption,
  selectSelectedCategoryIdsForSearch,
  setControlledSelectedCategoryIdsForSearch,
  setSearchIncludeVersions,
  setSearchKeyword,
  setSearchOption,
  setSelectedCategoryIdsForSearch,
  setStartOffsetForSearch,
} from '../../redux/store/content/slice';
import { useFilteredContentSuggestions } from './auto-complete-list/content-suggestion/useFilteredContentSuggestions';
import { SearchOption } from '../../redux/store/content/types';
import AutoCompleteList from './auto-complete-list/AutoCompleteList';
import useSearchSuggestions from './auto-complete-list/search-suggestion/useSearchSuggestions';

const SEARCHABLE_TEXT_MIN_LENGTH = 3;

type ISearchFieldInNavbarProps = Readonly<{
  searchFilterOpened: boolean;
  setSearchFilterOpened: (opened: boolean) => void;
}>;

function SearchFieldInNavbar({
  searchFilterOpened,
  setSearchFilterOpened,
}: ISearchFieldInNavbarProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchKeyword = useAppSelector(selectSearchKeyword);
  const selectedCategoryIds = useAppSelector(
    selectSelectedCategoryIdsForSearch,
  );
  const searchOption = useAppSelector(selectSearchOption);
  const includeVersions = useAppSelector(selectSearchIncludeVersions);
  const inputRef = useRef<HTMLInputElement>(null);
  const autoCompleteListRef = useRef<HTMLDivElement>(null);
  const [showAutoCompleteList, setShowAutoCompleteList] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const contentSuggestions = useFilteredContentSuggestions(searchKeyword);
  const searchSuggestions = useSearchSuggestions(searchKeyword);
  const totalSuggestions = contentSuggestions.length + searchSuggestions.length;

  // Reset activeIndex when suggestions or searchKeyword changes
  useEffect(() => {
    setActiveIndex(-1);
  }, [searchKeyword, contentSuggestions.length]);

  const submitSearch = () => {
    if (searchKeyword.trim() !== '') {
      setSearchFilterOpened(false);
      setShowAutoCompleteList(false);
      dispatch(setStartOffsetForSearch(0));
      navigate(
        getSearchUrl(
          searchKeyword,
          searchOption,
          includeVersions,
          selectedCategoryIds,
        ),
      );
    }
  };

  const autoCompleteListHidden = !(
    showAutoCompleteList &&
    !searchFilterOpened &&
    searchKeyword.length >= SEARCHABLE_TEXT_MIN_LENGTH
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (totalSuggestions > 0) {
        setActiveIndex((prev) => (prev + 1) % totalSuggestions);
      }
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (activeIndex === 0) {
        setActiveIndex(-1);
        inputRef.current?.focus();
      }

      if (activeIndex > 0 && totalSuggestions > 0) {
        setActiveIndex(
          (prev) => (prev - 1 + totalSuggestions) % totalSuggestions,
        );
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      setShowAutoCompleteList(false);

      if (activeIndex < 0) {
        submitSearch();
      }
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setShowAutoCompleteList(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestionClick = () => {
    setShowAutoCompleteList(false);
  };

  const toggleSearchFilter = () => {
    setSearchFilterOpened(!searchFilterOpened);
  };

  useOnClickOutside(autoCompleteListRef, (e) => {
    if ((e.target as HTMLElement).id !== autoCompleteListRef.current?.id) {
      setShowAutoCompleteList(false);
    }
  });

  useEffect(() => {
    if (!location.pathname.includes('/search')) {
      dispatch(setSearchKeyword(''));
      dispatch(setSelectedCategoryIdsForSearch([]));
      dispatch(setControlledSelectedCategoryIdsForSearch([]));
      dispatch(setSearchOption(SearchOption.AllWords));
      dispatch(setSearchIncludeVersions(false));
    }
  }, [location.pathname]);

  return (
    <InputGroup
      aria-expanded={searchFilterOpened}
      aria-controls='SearchFilterContainer'
      className='ms-2 me-2 search-field-header'>
      <Form.Label className='visually-hidden' htmlFor='SearchKeywordInHeader'>
        {t('searchKeyword')}
      </Form.Label>
      <Form.Control
        id='SearchKeywordInHeader'
        ref={inputRef}
        className='border-0'
        required
        placeholder={`${t('search')}...`}
        onKeyDown={handleKeyDown}
        onClick={() => {
          setShowAutoCompleteList(true);
        }}
        onChange={(e) => {
          dispatch(setSearchKeyword(e.target.value));
          setShowAutoCompleteList(true);
        }}
        value={searchKeyword}
        type='text'
      />
      <AutoCompleteList
        ref={autoCompleteListRef}
        searchString={searchKeyword}
        activeIndex={activeIndex}
        hidden={autoCompleteListHidden}
        onKeyDown={handleKeyDown}
        onSuggestionClick={handleSuggestionClick}
      />
      {!location.pathname.includes('/search') && (
        <Button
          variant='light'
          className='search-filter-btn'
          onKeyDown={(e) => {
            if (searchFilterOpened) {
              if (e.key === 'Tab') {
                e.preventDefault();
                document
                  .getElementById('SearchFilterContainer')
                  ?.getElementsByTagName('button')?.[0]
                  .focus();
              }

              if (e.key === 'Escape') {
                setSearchFilterOpened(false);
              }
            }
          }}
          aria-expanded={searchFilterOpened}
          onClick={toggleSearchFilter}
          aria-label={t('openSearchFilter')}
          title={t('openSearchFilter')}>
          <Filter className='fs-3' aria-hidden />
        </Button>
      )}
      <Button
        className='bg-white border-0 search-btn'
        onClick={() => {
          submitSearch();
        }}
        aria-label={t('startSearch')}>
        <i className='icon-search header-active-background' aria-hidden />
      </Button>
    </InputGroup>
  );
}

export default SearchFieldInNavbar;
